// Picking rows works as in a spreadsheet. Ctrl/⌘-click adds or removes one row and becomes the
// anchor. Shift-click (or Shift+↑/↓) picks from the anchor to that row, and moving the far end
// again redraws that range rather than adding to it; rows picked before the anchor stay picked.
// With nothing picked yet, the anchor is the highlighted row.

export interface Picks {
  picked: Set<string>;
  /** where the current range starts and ends; null when nothing has been picked */
  anchor: string | null;
  end: string | null;
  /** the rows picked outside the current range */
  base: Set<string>;
}

export const NO_PICKS: Picks = { picked: new Set(), anchor: null, end: null, base: new Set() };

/** Ctrl/⌘-click: one row in or out, and the next range starts from it. */
export function toggle(p: Picks, id: string): Picks {
  const picked = new Set(p.picked);
  if (picked.has(id)) picked.delete(id); else picked.add(id);
  return { picked, anchor: id, end: id, base: picked };
}

/** Shift-click: the anchor's range now reaches `id`. `ids` are the rows in the order shown; `from` is the highlighted row. */
export function extend(p: Picks, ids: readonly string[], id: string, from: string | null): Picks {
  const anchor = p.anchor ?? from ?? id;
  const base = p.anchor ? p.base : new Set<string>();
  const a = ids.indexOf(anchor), b = ids.indexOf(id);
  if (a < 0 || b < 0) return { picked: new Set([...base, id]), anchor: id, end: id, base };
  return { picked: new Set([...base, ...ids.slice(Math.min(a, b), Math.max(a, b) + 1)]), anchor, end: id, base };
}

/** Shift+↑/↓: the far end of the range moves a row. */
export function step(p: Picks, ids: readonly string[], d: number, from: string | null): Picks | null {
  const end = p.end ?? from;
  if (!end || !ids.length) return null;
  const i = ids.indexOf(end);
  const to = ids[Math.max(0, Math.min(ids.length - 1, i < 0 ? 0 : i + d))]!;
  return extend(p, ids, to, from);
}
